import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { DynIcon } from "@/components/shared/DynIcon";
import { CTA } from "@/components/home/CTA";
import { useServices } from "@/hooks/useContent";
import { useSeo } from "@/components/shared/useSeo";

const HERO = "https://images.unsplash.com/photo-1563456019560-2b37aa7ad890?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Hizmetler() {
  const { data: services = [] } = useServices();
  useSeo({
    title: "Hizmetler | Sistem Elektrik ve Mühendislik",
    description: "Endüstriyel elektrik, elektrik taahhüt, otomasyon, pano sistemleri, projelendirme ve teknik servis hizmetleri.",
    canonical: "https://sistemmuhendislik.com/hizmetler",
  });

  return (
    <>
      <PageHero
        eyebrow="HİZMETLER"
        title="Uçtan uca mühendislik hizmetleri."
        subtitle="Endüstriyel ihtiyaçlarınıza özel, projeden uygulamaya bütünsel çözümler."
        image={HERO}
        crumb={[{ label: "Hizmetler" }]}
      />

      <section className="bg-light py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="border-t border-ink/12">
            {services.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
              >
                <Link
                  to={`/hizmetler/${s.slug}`}
                  data-testid={`hizmet-card-${s.slug}`}
                  className="group relative flex items-center gap-6 md:gap-12 py-8 md:py-12 border-b border-ink/12 overflow-hidden"
                >
                  {/* hover image reveal (desktop) */}
                  <div className="hidden lg:block absolute inset-y-0 right-0 w-[40%] overflow-hidden pointer-events-none">
                    <img
                      src={s.image}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover opacity-0 scale-105 translate-x-6 group-hover:opacity-100 group-hover:translate-x-0 group-hover:scale-100 transition-all duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-light via-light/40 to-transparent" />
                  </div>

                  <span className="relative font-head font-extrabold text-4xl md:text-6xl tabular-nums text-ink/12 group-hover:text-cyan transition-colors duration-500 w-16 md:w-28 shrink-0">
                    {s.number}
                  </span>
                  <div className="relative flex-1 min-w-0">
                    <div className="flex items-center gap-4">
                      <div className="hidden md:flex w-11 h-11 shrink-0 items-center justify-center border border-ink/15 text-tech group-hover:border-cyan group-hover:text-cyan transition-colors duration-500">
                        <DynIcon name={s.icon} className="w-5 h-5" />
                      </div>
                      <h3 className="font-head font-bold text-2xl md:text-4xl tracking-tight text-ink group-hover:text-navy transition-colors duration-500">
                        {s.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-ink/55 leading-relaxed max-w-xl md:pl-[60px]">{s.short_desc}</p>
                  </div>
                  <span className="relative w-12 h-12 md:w-14 md:h-14 shrink-0 flex items-center justify-center border border-ink/15 text-ink/40 group-hover:bg-cyan group-hover:border-cyan group-hover:text-navy transition-all duration-500">
                    <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
